import type { StoreCartShippingOption } from "@medusajs/types";
import type { PickupHub, PickupReservation } from "#/lib/medusa-types/pickup";

export function findPickupHub(
  hubs: PickupHub[],
  key: string,
): PickupHub | null {
  return hubs.find((hub) => hub.key === key) ?? null;
}

export function availablePickupHubs(
  hubs: PickupHub[],
  hpRestricted: boolean,
): PickupHub[] {
  return hubs.filter((hub) => !hpRestricted || hub.hp_allowed);
}

const CLOCK = /^(\d{2}):(\d{2})$/;

export function formatPickupClock(hhmm: string): string {
  const match = CLOCK.exec(hhmm);
  if (!match) return hhmm;
  const [, hours = "", minutes = ""] = match;
  const h = Number(hours);
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${minutes} ${h < 12 ? "AM" : "PM"}`;
}

export function pickupHubHours(hub: PickupHub): string {
  return `${formatPickupClock(hub.opens_at)} – ${formatPickupClock(hub.closes_at)}`;
}

export function formatList(
  items: string[],
  type: "conjunction" | "disjunction",
): string {
  return new Intl.ListFormat("en", { type }).format(items);
}

export function pickupHubNames(hubs: PickupHub[]): string[] {
  return hubs.map((hub) => hub.name);
}

export const PICKUP_OPTION_TITLE = "Pickup";

const PICKUP_TYPE_CODES: ReadonlySet<string> = new Set([
  "hub_pickup",
  "bgc_pickup",
]);

const PICKUP_OPTION_NAMES: ReadonlySet<string> = new Set([
  "pickup",
  "hg bgc hub pickup",
]);

export function isPickupShippingOption(
  option: Pick<StoreCartShippingOption, "name" | "type">,
): boolean {
  const code = option.type?.code?.trim().toLowerCase();
  if (code && PICKUP_TYPE_CODES.has(code)) return true;
  return PICKUP_OPTION_NAMES.has(option.name.trim().toLowerCase());
}

export const PICKUP_METADATA_KEYS = [
  "pickupReservationId",
  "pickupHub",
  "pickupLocation",
  "pickupDate",
  "pickupTimeSlotId",
  "pickupTime",
] as const;

export type PickupMetadata = Record<
  (typeof PICKUP_METADATA_KEYS)[number],
  string
>;

export function pickupMetadata(
  reservation: PickupReservation | null,
  hubs: PickupHub[],
): PickupMetadata {
  if (!reservation) {
    return {
      pickupReservationId: "",
      pickupHub: "",
      pickupLocation: "",
      pickupDate: "",
      pickupTimeSlotId: "",
      pickupTime: "",
    };
  }
  return {
    pickupReservationId: reservation.id,
    pickupHub: reservation.hub,
    pickupLocation: findPickupHub(hubs, reservation.hub)?.label ?? "",
    pickupDate: reservation.pickup_date,
    pickupTimeSlotId: reservation.time_slot.id,
    pickupTime: reservation.time_slot.name,
  };
}
